<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useSettingsNavConfig } from 'dashboard/composables/useSettingsNavConfig';
import { usePolicy } from 'dashboard/composables/usePolicy';
import SettingsSectionLayout from './components/SettingsSectionLayout.vue';

defineProps({
  keepAlive: {
    type: Boolean,
    default: true,
  },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { shouldShow } = usePolicy();
const { settingsNavChildren } = useSettingsNavConfig();

const hideSettingsInnerNav = computed(() =>
  route.matched.some(record => record.meta.hideSettingsInnerNav === true)
);

const findRouteByName = name => router.getRoutes().find(r => r.name === name);

const resolveRouteMeta = (to, key, fallback) => {
  if (!to) return fallback;
  if (to.params?.navigationPath) {
    return findRouteByName(to.params.navigationPath)?.meta?.[key] ?? fallback;
  }
  return router.resolve(to)?.meta?.[key] ?? fallback;
};

const isLinkAllowed = to =>
  shouldShow(
    '',
    resolveRouteMeta(to, 'permissions', []),
    resolveRouteMeta(to, 'installationTypes', [])
  );

const sections = computed(() =>
  settingsNavChildren.value
    .map(section => ({
      ...section,
      children: section.children
        .filter(leaf => leaf.to && isLinkAllowed(leaf.to))
        .map(leaf => ({
          ...leaf,
          activeOnRoutes: leaf.activeOn,
        })),
    }))
    .filter(section => section.children.length > 0)
);
</script>

<template>
  <SettingsSectionLayout
    :title="t('SIDEBAR.WORKSPACE_SETTINGS')"
    :aria-label="t('SIDEBAR.WORKSPACE_SETTINGS')"
    :sections="sections"
    :show-nav="!hideSettingsInnerNav"
  >
    <div
      class="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col basis-0 items-stretch"
    >
      <router-view v-slot="{ Component }">
        <keep-alive v-if="keepAlive">
          <component :is="Component" :key="route.fullPath" />
        </keep-alive>
        <component :is="Component" v-else :key="route.fullPath" />
      </router-view>
    </div>
  </SettingsSectionLayout>
</template>
